import { MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../../../lib/http/ApiError';
import { useGetRightsProfiles } from '../../../rights-profiles/hooks/useGetRightsProfiles';

export interface ProfileSelectorProps {
  /** Selected profile id (`profile_id`), or null when none is chosen yet. */
  value: string | null;
  /** Called with the chosen profile id. The parent owns the save (POST/PUT). */
  onChange: (profileId: string) => void;
  /**
   * External error to surface on the field — e.g. the 403 P5 message raised by the
   * user form (HOMCO-623) when a `users` caller picks a broader profile. Takes
   * precedence over the help/description.
   */
  errorMessage?: string;
  disabled?: boolean;
  required?: boolean;
  /**
   * Hide the built-in floating label (the host renders its own external label). The
   * field keeps an accessible name: when hidden, the profile label is applied as the
   * select's `aria-label`, since a `<label htmlFor>` cannot label MUI's non-native
   * (div) combobox.
   */
  hideLabel?: boolean;
  /** Input id, so a host-rendered external `<label htmlFor>` can bind to it. */
  id?: string;
  /** Show the selected profile's description as helper text (default `true`).
   * The user fiche passes `false` so the description stays hidden on the edit page. */
  showDescription?: boolean;
}

/** `PaginationDto` maximum: the selector lists the organization's profiles in one page. */
const PROFILE_SELECTOR_LIMIT = 100;

/**
 * Bloc B · Autorisations of the user form (HOMCO-623): choose a user's rights
 * profile. A controlled dropdown fed by GET /rights-profiles (via
 * `useGetRightsProfiles`); the locked Super Admin is offered like any other profile.
 * Every profile is offered, sorted by name: one request with the maximum page size
 * (HOMCO-1413 — beyond 100 profiles the list would be truncated, accepted limit).
 * The help line under the field mirrors the chosen profile's description (none
 * shown when it has no description). Chemin A (HOMCO-641) — no matrix preview.
 *
 * This component never saves: it reports the pick through `onChange` and only
 * reflects an `errorMessage` the form hands back (the real POST/PUT and the 403
 * handling live in HOMCO-623).
 */
export function ProfileSelector({
  value,
  onChange,
  errorMessage,
  disabled = false,
  required = true,
  hideLabel = false,
  id,
  showDescription = true,
}: ProfileSelectorProps) {
  const { t } = useTranslation('users');
  // No global toast: a load failure is shown inline under the field (loadError).
  const { data, loading, error } = useGetRightsProfiles(
    { limit: PROFILE_SELECTOR_LIMIT },
    { notifyOnError: false },
  );
  const profiles = data ?? [];

  const fieldLabel = t('profileSelector.label');
  const selected = profiles.find(p => p.id === value);
  // A 403 means the caller lacks rights_profiles.display — distinguish it from a
  // generic network/server failure so the message is actionable (ask an admin), not
  // "try again". Any other failure keeps the generic load error.
  const isForbidden = ApiError.isApiError(error) && error.isForbidden;
  const loadError = error
    ? isForbidden
      ? t('profileSelector.forbidden')
      : t('profileSelector.loadError')
    : undefined;
  // Priority: an explicit (form) error, then a load error, then the description
  // (when the host opted in via showDescription — the user fiche hides it).
  const description = showDescription ? selected?.description : undefined;
  const helperText = errorMessage ?? loadError ?? description ?? undefined;
  const isError = Boolean(errorMessage ?? loadError);

  return (
    <TextField
      id={id}
      select
      required={required}
      fullWidth
      size="small"
      label={hideLabel ? undefined : fieldLabel}
      value={value ?? ''}
      onChange={event => onChange(event.target.value)}
      disabled={disabled || loading}
      error={isError}
      helperText={helperText}
      // Non-native MUI Select is a div, not labelable by <label htmlFor>; when the
      // visible label is hidden, name the combobox via aria-label for screen readers.
      slotProps={hideLabel ? { select: { inputProps: { 'aria-label': fieldLabel } } } : undefined}
      // `text.primary` is white by theme convention (for coloured surfaces); on this
      // light surface force the dark `text.body` on the value, label and help — same
      // pattern as AuthCard / FilterPanel. The error helper stays red.
      sx={{
        '& .MuiSelect-select': { color: 'text.body' },
        '& .MuiInputLabel-root': { color: 'text.body' },
        '& .MuiFormHelperText-root:not(.Mui-error)': { color: 'text.body' },
      }}
    >
      {profiles.map(profile => (
        <MenuItem key={profile.id} value={profile.id} sx={{ color: 'text.body' }}>
          {profile.name}
        </MenuItem>
      ))}
    </TextField>
  );
}
